import { useState } from 'react'
import { build, type Sequence, type Track, type TrackKind } from '@easyedit/timeline'
import { useEditorStore } from '../../../stores/editorStore'

const KIND_LABELS: Record<string, string> = {
  video: 'Vídeo', audio: 'Áudio', image: 'Imagem', text: 'Texto',
  caption: 'Legenda', music: 'Música', sfx: 'SFX', adjustment: 'Ajuste', overlay: 'Overlay',
}

/** Tracks que carregam som — e portanto ganham fader. */
const AUDIBLE: TrackKind[] = ['audio', 'music', 'sfx', 'video']

export function TrackHeader({
  track,
  sequence,
  onMove,
}: {
  track: Track
  sequence: Sequence
  onMove: (trackId: string, direction: -1 | 1) => void
}) {
  const dispatch = useEditorStore((s) => s.dispatch)
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState(track.name)
  const ctx = { sequenceId: sequence.id }

  const update = (patch: Parameters<typeof build.updateTrack>[2]) =>
    void dispatch(build.updateTrack(ctx, track.id, patch))

  function commitName() {
    setRenaming(false)
    const name = draft.trim()
    if (name && name !== track.name) update({ name })
    else setDraft(track.name)
  }

  function remove() {
    // Apagar uma track leva os clips junto. É reversível com Ctrl+Z (o inverso é um
    // ADD_TRACK com a track inteira), mas perder trabalho sem aviso não é aceitável.
    if (track.items.length > 0) {
      const ok = window.confirm(
        `Remover a track "${track.name}" e os ${track.items.length} clips dela?`,
      )
      if (!ok) return
    }
    void dispatch(build.removeTrack(ctx, track.id))
  }

  const ordered = [...sequence.tracks].sort((a, b) => a.index - b.index)
  const position = ordered.findIndex((t) => t.id === track.id)

  return (
    <div
      className="border-b border-ink-800 px-2 py-1 flex flex-col justify-center gap-1 group/track"
      style={{ height: track.height }}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        {renaming ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName()
              if (e.key === 'Escape') { setDraft(track.name); setRenaming(false) }
            }}
            className="min-w-0 flex-1 bg-ink-900 border border-accent rounded px-1 py-0.5
              font-mono text-2xs text-ink-100 focus:outline-none"
          />
        ) : (
          <button
            onDoubleClick={() => { setDraft(track.name); setRenaming(true) }}
            title="Clique duplo para renomear"
            className="font-mono text-2xs text-ink-200 truncate hover:text-accent transition-colors"
          >
            {track.name}
          </button>
        )}
        <span className="text-2xs text-ink-500 truncate">{KIND_LABELS[track.kind] ?? track.kind}</span>
      </div>

      <div className="flex items-center gap-0.5">
        <IconToggle
          active={track.hidden}
          onClick={() => update({ hidden: !track.hidden })}
          title={track.hidden ? 'Mostrar track' : 'Ocultar track'}
          activeClass="text-danger"
        >
          {track.hidden ? '⊘' : '👁'}
        </IconToggle>
        <IconToggle
          active={track.muted}
          onClick={() => update({ muted: !track.muted })}
          title={track.muted ? 'Reativar áudio' : 'Silenciar track'}
          activeClass="text-danger"
        >
          {track.muted ? '🔇' : '🔊'}
        </IconToggle>
        <IconToggle
          active={track.solo}
          onClick={() => update({ solo: !track.solo })}
          title="Solo — toca apenas esta track"
          activeClass="text-accent"
        >
          S
        </IconToggle>
        <IconToggle
          active={track.locked}
          onClick={() => update({ locked: !track.locked })}
          title={track.locked ? 'Destravar track' : 'Travar track'}
          activeClass="text-accent"
        >
          {track.locked ? '🔒' : '🔓'}
        </IconToggle>

        {/* Ordem e remoção aparecem no hover: são raros ao lado dos quatro de cima,
            e ocupar espaço permanente com eles atrapalharia o uso frequente. */}
        <div className="ml-auto flex items-center gap-0.5 opacity-0 group-hover/track:opacity-100 transition-opacity">
          <IconToggle
            active={false}
            disabled={position >= ordered.length - 1}
            onClick={() => onMove(track.id, 1)}
            title="Subir uma camada"
            activeClass=""
          >
            ▲
          </IconToggle>
          <IconToggle
            active={false}
            disabled={position <= 0}
            onClick={() => onMove(track.id, -1)}
            title="Descer uma camada"
            activeClass=""
          >
            ▼
          </IconToggle>
          <IconToggle active={false} onClick={remove} title="Remover track" activeClass="">
            ✕
          </IconToggle>
        </div>
      </div>

      {/* O fader só aparece onde há altura para ele — numa track de 44px, um slider
          espremido entre o nome e os botões é pior que nenhum. */}
      {AUDIBLE.includes(track.kind) && track.height >= 56 && (
        <input
          type="range"
          min={0}
          max={2}
          step={0.01}
          value={track.volume}
          onChange={(e) => update({ volume: Number(e.target.value) })}
          title={`Volume da track: ${Math.round(track.volume * 100)}%`}
          className="w-full h-1 accent-accent cursor-pointer"
        />
      )}
    </div>
  )
}

function IconToggle({
  active,
  onClick,
  title,
  activeClass,
  disabled,
  children,
}: {
  active: boolean
  onClick: () => void
  title: string
  activeClass: string
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      className={`w-5 h-5 grid place-items-center rounded text-2xs transition-colors
        ${active ? activeClass : 'text-ink-500'} hover:bg-ink-750
        disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent`}
      onClick={onClick}
      title={title}
      disabled={disabled}
      aria-pressed={active}
    >
      {children}
    </button>
  )
}
